import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Grid,
  Heading,
  Tab,
  TabList,
  Tabs,
  VStack
} from '@chakra-ui/react';
import useAuthRole from '@hooks/useAuthRole';
import ContentLayout from '@layouts/ContentLayout';
import { kebabCaseToSpaceSeperate } from '@utils';
import { useDispatch, useSelector } from 'react-redux';
import AgentDashboard from '@views/OutboundCallingDashboard/AgentDashboard';
import AdminDashboard from '@views/OutboundCallingDashboard/AdminDashboard';
import {
  TAB_STATES,
  TAB_STATES_ACCESS_ROLES,
  OUTBOUND_CALLING_DASHBOARD_STRINGS
} from '@views/OutboundCallingDashboard/constant';
import { generalConfig as config } from '@views/OutboundCallingDashboard/utils';

/**
 * Main Outbound Calling Dashboard Component
 * Provides tabbed interface for Agent and Admin views
 */
const OutboundCallingDashboard = () => {
  const { hasAnyAuthority } = useAuthRole();
  const dispatch = useDispatch();

  const { data: configData, isLoading: isConfigLoading, error: configError } = useSelector(
    (state) => state.outboundCalling.config
  );

  /**
   * Local states
   */
  const availableTabs = Object.keys(TAB_STATES).filter((tabState) =>
    hasAnyAuthority(...TAB_STATES_ACCESS_ROLES[tabState])
  );

  // Initialize selectedTab to the first available tab based on user's role
  const [selectedTab, setSelectedTab] = useState(
    availableTabs.length > 0 ? TAB_STATES[availableTabs[0]] : TAB_STATES.AGENT
  );

  useEffect(() => {
    if (!configData && !isConfigLoading && !configError) {
      fetchDashboardConfig();
    }
  }, [dispatch, configData, isConfigLoading, configError]);

  const fetchDashboardConfig = useCallback(() => {
    dispatch(
      config.actions.getDashboardConfig()
    );
  }, [dispatch]);

  /**
   * Handle tab change
   */
  const handleTabChange = (index) => {
    const tabKey = availableTabs[index];
    setSelectedTab(TAB_STATES[tabKey]);
  };


  return (
    <ContentLayout>
      <Grid
        gridTemplateColumns={{
          base: '1fr',
          lg: `10fr 0fr 0fr`
        }}
      >
        {/* Main Column */}
        <VStack
          overflowY={'hidden'}
          overflowX={'hidden'}
          h={{ lg: '90vh' }}
          pt={10}
          pb={4}
          px={{ base: 4, sm: 6, lg: 10 }}
          alignItems={'stretch'}
          gap={4}
        >
          {/* Dashboard Header */}
          <Heading size="lg" color="colorPrimary">
            {OUTBOUND_CALLING_DASHBOARD_STRINGS.HEADING}
          </Heading>

          {/* Tabs */}
          <Tabs
            variant={'enclosed'}
            size={'md'}
            onChange={handleTabChange}
            defaultIndex={availableTabs.indexOf(selectedTab)}
            display="flex"
            flexDirection="column"
            overflow="hidden"
          >
            <TabList color={'whiteAlpha.700'} borderColor={'whiteAlpha.500'}>
              {availableTabs.map((tabState, ind) => (
                <Tab
                  key={ind}
                  _selected={{
                    color: 'brand.500',
                    border: '1px solid',
                    borderColor: 'whiteAlpha.500',
                    borderBottom: '1px solid black'
                  }}
                  _focus={{
                    boxShadow: 'none'
                  }}
                >
                  {kebabCaseToSpaceSeperate(tabState)}
                </Tab>
              ))}
            </TabList>
          </Tabs>

          {/* Tab Content - Conditional Rendering */}
          <Box
            flex="1"
            overflowY="auto"
            overflowX="hidden"
          >
            {selectedTab === TAB_STATES.AGENT && <AgentDashboard />}
            {selectedTab === TAB_STATES.ADMIN && <AdminDashboard />}
          </Box>

        </VStack>
      </Grid>
    </ContentLayout>
  );
};

export default OutboundCallingDashboard;
export {
  OutboundCallingAgentsDashboard,
  OutboundCallingOpenTicketsDashboard,
  OutboundCallingClosedTicketsDashboard
} from './AdminDashboard';
